import * as React from 'react';
import { useContext } from 'react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';

import { ArrowDownIcon, ArrowUpIcon, CompanyAvatarIcon } from 'components/common/Icons/Icons';
import Button from 'components/common/Button/Button';
import { ApplicationRoute } from 'routes/routes.enums';
import { PermissionName } from 'components/permissions/permissions.enum';
import { hasPermission } from 'components/permissions/permissions.utils';
import { RootStoreContext } from 'index';

import { useBurgersMenu } from './useBurgersMenu';
import styles from './BurgersMenu.module.scss';

interface CompanyMenuProps {
  companyName: string;
}

const CompanyMenu = ({ companyName }: CompanyMenuProps) => {
  const { anchorEl, isMenuOpen, handleClick, handleClose, goTo, location } = useBurgersMenu();

  const { authStore } = useContext(RootStoreContext);
  const userPermissions = authStore.account?.permissions ?? [];
  const mdaSignedPermissions = authStore.account?.mdaSignedPermissions;

  const companyMenuItems = [
    {
      label: 'Company Profile',
      route: ApplicationRoute.CompanyProfile,
      requiredPermissions: [PermissionName.CompanyInformationView],
    },
    {
      label: 'Company Notifications',
      route: ApplicationRoute.CompanyNotifications,
      requiredPermissions: [PermissionName.CompanyNotificationsUpdate],
    },
    {
      label: 'Account Status',
      route: ApplicationRoute.AccountStatus,
      requiredPermissions: [PermissionName.CompanyVerificationUpdate],
    },
    {
      label: 'User Management',
      route: ApplicationRoute.UsersManagement,
      requiredPermissions: [PermissionName.UserView],
    },
    {
      label: 'Role Management',
      route: ApplicationRoute.RoleManagement,
      requiredPermissions: [PermissionName.RoleUpdate],
    },
  ];

  const visibleCompanyMenuItems = companyMenuItems.filter((item) =>
    mdaSignedPermissions
      ? hasPermission(mdaSignedPermissions, item.requiredPermissions)
      : hasPermission(userPermissions, item.requiredPermissions),
  );

  if (!visibleCompanyMenuItems.length) {
    return (
      <div className={styles.button}>
        <CompanyAvatarIcon className={styles.icon} />
        <Tooltip title={companyName} placement="bottom">
          <span className={styles.ellipsis}>{companyName}</span>
        </Tooltip>
      </div>
    );
  }

  return (
    <>
      <Button
        className={styles.button}
        onClick={handleClick}
        variant="underline"
        aria-controls={isMenuOpen ? 'company-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
      >
        <CompanyAvatarIcon className={styles.icon} />

        <Tooltip title={companyName} placement="bottom">
          <span className={styles.ellipsis}>{companyName}</span>
        </Tooltip>

        {isMenuOpen ? (
          <ArrowUpIcon className={clsx(styles.arrowIcon, styles.arrowOpenIcon)} />
        ) : (
          <ArrowDownIcon className={styles.arrowIcon} />
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        id="company-menu"
        open={isMenuOpen}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {visibleCompanyMenuItems.map(({ label, route }) => (
          <MenuItem
            key={label}
            onClick={() => (route ? goTo(route)() : handleClose())}
            className={clsx(styles.menuItem, {
              [styles.menuItemActive]: route && location.pathname === route,
            })}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default CompanyMenu;
